import capitalize from 'lodash/capitalize';
import { forwardRef, Key, useCallback, useMemo, useState } from 'react';
import { Autocomplete, Button, Select } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import Icon from '~/components/data-display/Icon';
import { SkeletonLoader } from '~/components/data-display/SkeletonLoader/SkeletonLoader';
import { ListBoxItems, ListBoxPopoverContainer } from '~/components/inputs/Select/ListBoxPopover';
import SearchField from '~/components/inputs/TextField/SearchField';
import { HStack, VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import IconChevronDown from '~/icons/icon-chevron-down.svg?react';
import { useAllBookkeepingAccountsQuery } from '~/services/bookkeeping/bookkeeping-accounts';
import { BookkeepingAccount } from '~/types/general-bookkeeping';
import {
  bookkeepingAccountDescription,
  bookkeepingAccountLabel,
  checkBookkeepingAccountMatch,
} from '~/utils/bookkeeping/bookkeeping-account';
import styles from './BookkeepingAccountField.module.scss';

interface BookkeepingAccountFieldProps {
  autoFocus?: boolean;
  invalid: boolean;
  onChange: (value: BookkeepingAccount | null) => void;
  readOnly?: boolean;
  value: string;
  isLoading?: boolean;
}

const BookkeepingAccountField = forwardRef<HTMLDivElement, BookkeepingAccountFieldProps>(
  ({ autoFocus, invalid, onChange, readOnly, value, isLoading }, ref) => {
    const { t } = useTranslation('bookkeeping', { keyPrefix: 'inputs.account_field' });
    const { data: accounts } = useAllBookkeepingAccountsQuery({ hasChildren: false });
    const selectedAccount = (accounts ?? []).find((account) => account.id === value);
    const [textInput, setTextInput] = useState<string>('');

    const options = useMemo(
      () =>
        (accounts ?? [])
          .filter((account) => checkBookkeepingAccountMatch(account, textInput))
          .slice(0, 200)
          .map((account) => ({
            id: account.id,
            label: bookkeepingAccountLabel(account),
            description: bookkeepingAccountDescription(account),
            textValue: account.code,
          })),
      [accounts, textInput],
    );

    const handleChange = useCallback(
      (id: Key | null) => {
        const account = (accounts ?? []).find((c) => c.id === id);
        onChange(account ?? null);
        setTextInput('');
      },
      [accounts, onChange],
    );

    return (
      <Select
        autoFocus={autoFocus}
        aria-label={t('label')}
        className={styles.combobox}
        isDisabled={readOnly}
        isInvalid={invalid}
        onSelectionChange={handleChange}
        ref={ref}
        selectedKey={value}
      >
        <Button className={styles.button}>
          {isLoading && !selectedAccount ? (
            <BookkeepingAccountSkeleton />
          ) : (
            <HStack align="center" justify="space-between" className={styles.buttonContent}>
              <VStack className={styles.accountDetails}>
                <Text variant="caption" weight="semibold" className={styles.code}>
                  {selectedAccount?.code}
                </Text>
                <Text variant="body2" weight="regular" ellipsis>
                  {capitalize(selectedAccount?.name)}
                </Text>
              </VStack>
              {!readOnly && <Icon component={IconChevronDown} size="sm" className={styles.chevron} />}
            </HStack>
          )}
        </Button>
        <ListBoxPopoverContainer>
          <Autocomplete>
            <SearchField
              autofocus
              onChange={setTextInput}
              label={t('label')}
              placeholder={t('search')}
              hideLabel
              className={styles.searchField}
              value={textInput}
            />

            <ListBoxItems options={options} />
          </Autocomplete>
        </ListBoxPopoverContainer>
      </Select>
    );
  },
);

const BookkeepingAccountSkeleton = () => {
  return (
    <div className={styles.skeletonSubfield}>
      <SkeletonLoader count={1} height="12px" className={styles.skeleton} />
      <SkeletonLoader count={1} height="18px" className={styles.skeleton} />
    </div>
  );
};

export default BookkeepingAccountField;
